import { useCallback } from "react";
import { toast } from "sonner";

import { useBilling, useBillingPortal, useCheckout } from "@/hooks/use-billing";
import { notifyError, type ErrorAction } from "@/lib/errors";
import type { BillingItem } from "@/lib/api";

/** Real inbox, matches the marketing site's Contact footer
 * (landing/src/content.ts CONTACT_EMAIL) — kept in sync by hand since the
 * landing site is a separate package. */
export const SUPPORT_EMAIL = "graphviewmind@gmail.com";

/** Resolves a {@link ErrorAction}/billing reason into an actual click
 * handler. Centralized so ErrorState, RestrictedState, and EngineStatus never
 * invent three different ideas of what "Sign in again" or "Upgrade" does.
 *
 * `signin` and `home` both reload the page: sign-in is a full-page gate in
 * this app (see `hooks/use-auth-gate.ts`), not a modal that can be opened
 * from anywhere, and there is no separate "home" route in this single-page
 * studio — a reload is the one recovery that is always safe and always
 * available without reaching into another owner's component tree. Local-first
 * storage means server state is never lost by a reload; the editor's own
 * autosave/keepalive paths already guard unsaved in-memory edits. */
export function useRecoveryAction() {
  const billing = useBilling();
  const checkout = useCheckout();
  const portal = useBillingPortal();

  const billingEnabled = billing.data?.enabled ?? null; // null = not yet known
  const billingActive = billing.data?.status === "active" || billing.data?.status === "trialing";

  /** Start Stripe Checkout for the given add-on(s) — or open the customer
   * portal instead when there's already a live subscription to modify — and
   * redirect to the returned hosted URL. No-ops (with a toast) when billing
   * isn't configured for this deployment rather than firing a request that
   * can only 503. */
  const openBilling = useCallback(
    (items: BillingItem[] = ["storage", "models"]) => {
      if (billingEnabled === false) {
        // Known-safe, app-authored copy — bypass toUserError (it deliberately
        // never shows a raw Error.message) and toast it directly.
        toast.error("Upgrades aren't available", {
          description: "This deployment doesn't offer self-serve billing.",
        });
        return;
      }
      const request = billingActive ? portal.mutateAsync() : checkout.mutateAsync(items);
      request
        .then((res) => {
          window.location.href = res.url;
        })
        .catch((err: unknown) => notifyError(err));
    },
    [billingEnabled, billingActive, checkout, portal],
  );

  const reload = useCallback(() => window.location.reload(), []);
  const contactSupport = useCallback(() => {
    window.location.href = `mailto:${SUPPORT_EMAIL}`;
  }, []);

  /** Map a {@link ErrorAction} into a click handler, or `undefined` when the
   * action can't be performed right now (e.g. billing disabled) — callers
   * should hide the button in that case rather than render a dead one. */
  const resolve = useCallback(
    (action: ErrorAction | undefined, onRetry?: () => void): (() => void) | undefined => {
      if (!action) return undefined;
      switch (action.kind) {
        case "retry":
          return onRetry ?? reload;
        case "signin":
        case "home":
          return reload;
        case "support":
          return contactSupport;
        case "billing":
          return billingEnabled === false ? undefined : () => openBilling();
        default:
          return undefined;
      }
    },
    [reload, contactSupport, billingEnabled, openBilling],
  );

  return { resolve, openBilling, signIn: reload, contactSupport, billingEnabled, billingActive };
}
